import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { Button, Grid } from '@material-ui/core';
import DataTable from "../../../components/DataTable/DataTable";
import Search from "../../../components/Search/Search";
import CustomizeView from "../../../components/DataTable/CustomizeView/CustomizeView";
import Filters from "../../../components/DataTable/Filters/Filters";
import DeleteConfirm from '../../../components/DataTable/DeleteConfirm';
import triggerService from "../../../services/triggerService/triggerService";
import AddWebhookRule from "./addWebhookRule/AddWebhookRule";
import EditOutlined from "@material-ui/icons/EditOutlined";
import DeleteWebhook from "./deleteWebhook/DeleteWebhook";
import { alertState, personalizationState } from "./../../../recoil-store/recoil-state";
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { convertToDateFormat } from '../../../helpers/helper';

let deletedAutoms = [];
let setSelectedAutomRow;

const WebhookRule = () => {
    const { t } = useTranslation();
    const personalization = useRecoilValue(personalizationState);

    const [data, setData] = useState([]);
    const [refresh, setRefresh] = useState(false);
    const [searchData, setSearchData] = useState([])
    const [loading, setLoading] = useState(false); // default true
    const [customColumn, setCustomColumn] = useState([])
    const [openCustomizeView, setOpenCustomizeView] = useState(false);
    const [openFilters, setOpenFilters] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [openAdd, setOpenAdd] = useState({ type: '', id: '' });
    const [deleteOpen, setDeleteOpen] = useState(false);
    const setAlert = useSetRecoilState(alertState);

    useEffect(() => {
        fetchData().then(r => r)
    }, [refresh]);

    const fetchData = async () => {
        setLoading(true);
        const resp = await triggerService.getRules();
        if (resp) {
            if (resp?.data) {
                if (Array.isArray(resp?.data)) {
                    setData(resp.data);
                    setSearchData(resp?.data)
                }
                else {
                    setData([]);
                    setSearchData([])
                }
            }
        }
        setLoading(false);
    }

    const handleRowsDelete = useCallback((rowsDeleted, dataArray, setSelectedRows) => {
        setOpenDelete(true);
        deletedAutoms = [];
        for (let i of rowsDeleted.data) {
            deletedAutoms.push(searchData[i.dataIndex].id);
        }
        setSelectedAutomRow = setSelectedRows;
    }, [searchData, openDelete]);


    function handleOpen(open, setState) { setState(!open); }

    const deleteAutoms = async () => {
        let ids = deletedAutoms;
        setLoadingDelete(true);
        const response = await triggerService.deleteRules(ids);
        if (response) {
            if (response.status) {
                if (!deleteOpen) {
                    setSelectedAutomRow([]);
                }
                fetchData().then(r => r)
            }
            setAlert(alert => ({ ...alert, show: true, message: t("MESSAGES.WEBHOOK." + response.message), severity: response.status ? 'success' : 'error' }))
        } else {
            setAlert(alert => ({ ...alert, show: true, message: t('GENERAL.NETWORK-ERROR'), severity: 'error' }))
        }
        setLoadingDelete(false);
    };

    const handleDelete = (id) => {
        deletedAutoms = [];
        deletedAutoms.push(id);
        setDeleteOpen(!deleteOpen);
    }

    useEffect(() => {
        const column = [
            {
                id: 0,
                name: "id",
                label: " ",
                options: {
                    filter: false,
                    sort: false,
                    customBodyRenderLite: (dataIndex, rowIndex) => {
                        if (searchData && searchData[dataIndex]) {
                            return <span>{dataIndex + 1}.</span>;
                        }
                    },
                },
            },
            {
                id: 1,
                name: "name",
                label: t("CONTENT.AUTOMS-LIST.AUTOM-NAME"),
                options: { filter: true, sort: true },
            },
            {
                id: 2,
                name: "description",
                label: t("CONTENT.AUTOMS-LIST.DESCRIPTION"),
                options: {
                    filter: true, sort: true,
                    customBodyRenderLite: (dataIndex) => {
                        if (searchData[dataIndex]) {
                            if (searchData[dataIndex]?.description === '') {
                                searchData[dataIndex].description = "-";
                            }
                            return searchData[dataIndex]?.description;
                        }
                    },
                }
            },
            {
                id: 3,
                name: "updated_at",
                label: t("CONTENT.AUTOMS-LIST.UPDATED-AT"),
                options: {
                    filter: true, sort: true, display: true,
                    customBodyRenderLite: (dataIndex) => {
                        if (searchData[dataIndex]) {
                            return convertToDateFormat(searchData[dataIndex]?.updated_at, personalization.date_format);
                        }
                    },
                }
            },
            {
                id: 4,
                name: "actions",
                label: t("CONTENT.AUTOMS-LIST.ACTIONS"),
                options: {
                    filter: false,
                    sort: false,
                    customBodyRenderLite: (dataIndex) => {
                        // Show edit icon if the status of the current autom is true and the user has less autom than specified in the license
                        if (searchData[dataIndex]) {
                            return (
                                <div style={{ display: "flex" }}>
                                    <Button size="small" onClick={() => setOpenAdd({ type: "edit", id: searchData[dataIndex]?.id })}>
                                        <EditOutlined fontSize="small" color="disabled" style={{ margin: '0 5px 0 -20px' }} />
                                        <span className="db-sublabel">{t('CONTENT.WEBHOOK.EDIT')}</span>
                                    </Button>
                                    <Button size="small" onClick={() => handleDelete(searchData[dataIndex].id)}>
                                        <span className="db-sublabel">{t('CONTENT.WEBHOOK.DELETE')}</span>
                                    </Button>
                                </div>
                            );
                        }
                    },
                },
            },
        ]
        setCustomColumn(column)

    }, [searchData, t, setCustomColumn])


    return (
        <div id="credential-container" className='data-body-container'>
            <Search data={data} setSearchData={setSearchData} searchKey={['name', 'description']} xs={11} sm={11} md={11} lg={4} xl={4} spacing={2}>
                <Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
                    <Button className="searchbar-button" onClick={() => setOpenAdd({ type: "add", id: "" })}>{t('CONTENT.WEBHOOK.ADD-RULE')}</Button>
                    <Button className="searchbar-button" onClick={() => handleOpen(openCustomizeView, setOpenCustomizeView)}>{t('CONTENT.DATATABLE.TOOLBAR.CUSTOMIZE-VIEW')}</Button>
                    <Button className="searchbar-button" onClick={() => handleOpen(openFilters, setOpenFilters)}>{t('CONTENT.DATATABLE.TOOLBAR.FILTERS')}</Button>
                </Grid>
            </Search>

            <Grid
                item
                sm={12}
                data-test="listautom-page"
            >
                <DataTable
                    data-test="automlist-datatable"
                    customColumns={customColumn}
                    onRowsDelete={handleRowsDelete}
                    selectableRows="multiple"
                    data={searchData ? searchData : []}
                    loading={loading}
                    loadingDelete={loadingDelete}
                    selectedRowMessage={"CONTENT.WEBHOOK.SELECTED-MESSAGE"}
                    perPageMessage={"CONTENT.WEBHOOK.PER-PAGE-MESSAGE"}
                />

                {openDelete && <DeleteConfirm setOpenDelete={setOpenDelete} message={t("CONTENT.AUTOMS-LIST.DELETE-WARNING")} deleteFunc={deleteAutoms} />}
                {openCustomizeView && <CustomizeView setOpen={setOpenCustomizeView} customColumn={customColumn} setCustomColumn={setCustomColumn} />}
                {openFilters && <Filters setOpen={setOpenFilters} customColumn={customColumn} setCustomColumn={setCustomColumn} />}
                {(openAdd?.type === "add" || openAdd?.type === "edit") && <AddWebhookRule openAdd={openAdd} setOpen={setOpenAdd} setRefresh={setRefresh} />}
                {deleteOpen && <DeleteWebhook setOpen={setDeleteOpen} deleteFunc={deleteAutoms} setRefresh={setRefresh} />}
            </Grid>

        </div>
    );
};

export default WebhookRule;
